import { useState, useEffect } from 'react';
import { useAccount } from '@/account/useAccount';
import { PremiumDataModal, usePremiumDataModal } from '@/components/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { ExportModal, PremiumPaymentSuccessModal } from '@/reports/index';
import { LizanoListInfo } from '@/reports/sections/ListsSection/lists/lists';
import {
  Button,
  Stack,
  Icons,
  Text,
  Select,
  LoadingReportCard,
} from '@/theme/index';
import { checkSingleReportVariations } from '@/utils/checkSingleReportVariations';
import { pluralizeString, titleize } from '@/utils/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { AccountListsList } from './AccountListsList';
import {
  EmptySectionCard,
  FlexContainer,
  HorizontalLine,
  LeftContainer,
  SelectOption,
  Title,
} from './components';
import { useAccountLists } from './useAccountLists/useAccountLists';

type Option = {
  id: number;
  label: string;
  value: string;
  display: string;
};

const defaultOption: Option = {
  id: 0,
  label: 'All',
  value: 'all',
  display: 'All',
};

interface AccountListsProps {
  lists?: LizanoListInfo[];
  title?: string;
  customHeaderActions?: JSX.Element | JSX.Element[];
  isBrandLoading?: boolean;
}

export function AccountLists({
  title,
  customHeaderActions,
  isBrandLoading,
}: AccountListsProps) {
  const { isMobile, isDesktop } = useScreenSize();
  const {
    featureFlags: { enableListExport },
    variationPlanNames,
    internationalPlanNames,
  } = useAppConstantsContext();
  const { data: account } = useAccount(true);
  const { routingUtils } = useAppConfigContext();
  const { navigate } = routingUtils;
  const { data: lists, isLoading } = useAccountLists();
  const [openExportModal, setOpenExportModal] = useState(false);
  const [accountLists, setAccountLists] = useState<LizanoListInfo[]>([]);
  const [selectOptions, setSelectOptions] = useState<SelectOption[]>([
    defaultOption,
  ]);
  const [selectedOption, setSelectedOption] = useState(defaultOption.value);
  const [selectedOptionID, setSelectedOptionID] = useState(defaultOption.id);

  const [currentlyDisplayedLists, setCurrentlyDisplayedLists] = useState<
    LizanoListInfo[]
  >([]);

  const reportVariations =
    account && internationalPlanNames
      ? checkSingleReportVariations(
          account,
          variationPlanNames,
          internationalPlanNames
        )
      : null;
  const isInSingleReportVariation =
    reportVariations?.isInSingleReportVariation ?? false;

  const { isModalOpen, closeModal, triggerModal } = usePremiumDataModal();
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [isSuccessModaVisible, setIsSuccessModaVisible] =
    useState<boolean>(false);

  const openSuccessModal = () => {
    setIsSuccessModaVisible(true);
  };

  const closeModals = () => {
    setIsSuccessModaVisible(false);
    setPaymentShowModalNumber(0);
  };

  const onSuccessPaymentHandler = () => {
    closeModals();
    navigate('/dashboard');
  };

  const accountListsLength = accountLists?.length;

  const createOptions = (lists: LizanoListInfo[]) => {
    return lists?.map((list: LizanoListInfo) => {
      return {
        id: list?.id,
        label: list?.name || '',
        value: list?.name || '',
        display: list?.name ? titleize(list.name) : '',
      } as Option;
    });
  };

  const handleDeleteOrDefaultOption = (lists: LizanoListInfo[]) => {
    setSelectedOption(defaultOption.value);
    setSelectedOptionID(defaultOption.id);
    setCurrentlyDisplayedLists(lists);
  };

  const handleUpdateOption = (options: Option[], lists: LizanoListInfo[]) => {
    const option =
      options.find((option) => {
        return selectedOptionID === option.id;
      }) || defaultOption;
    setSelectedOption(option.value);
    const filteredLists = lists.filter(
      (list: LizanoListInfo) => list.name === option.value
    );
    setCurrentlyDisplayedLists(filteredLists);
    setSelectedOptionID(filteredLists[0].id);
  };

  useEffect(() => {
    if (lists && lists.length > 0) {
      const options = createOptions(lists);
      if (options[0].label) {
        setSelectOptions([defaultOption, ...options]);
        if (
          lists.length !== accountListsLength ||
          selectedOption === defaultOption.value
        ) {
          handleDeleteOrDefaultOption(lists);
        } else {
          handleUpdateOption(options, lists);
        }
      }
      setAccountLists(lists);
    } else {
      setSelectOptions([defaultOption]);
      handleDeleteOrDefaultOption([]);
      setAccountLists([]);
    }
  }, [lists]);

  const handleSelect = (value: string) => {
    setSelectedOption(value);
    if (value === defaultOption.value && lists) {
      setCurrentlyDisplayedLists(lists as LizanoListInfo[]);
      setSelectedOptionID(defaultOption.id);
      return;
    }
    const filteredLists = accountLists.filter(
      (list: LizanoListInfo) => list.name === value
    );
    setCurrentlyDisplayedLists(filteredLists);
    setSelectedOptionID(filteredLists[0].id);
  };

  return (
    <>
      <FlexContainer>
        <Title variant="h1">
          <Icons.List
            color="primary"
            sx={{ marginRight: '4px', height: '30px', width: '30px' }}
            fontSize="large"
          />
          {title
            ? titleize(title, { validateCaps: false })
            : 'Saved Reports and Lists'}
        </Title>
        <Stack
          direction={isDesktop ? 'row' : 'column'}
          justifyContent="space-between"
          alignItems="end"
        >
          {!isMobile && (
            <LeftContainer>
              <Text my={1} variant="subtitle1">
                Showing {currentlyDisplayedLists.length}{' '}
                {pluralizeString('list', currentlyDisplayedLists.length)}
              </Text>
              <Select
                id="account-lists-select"
                options={selectOptions}
                onOptionSelect={handleSelect}
                defaultValue={defaultOption.value}
              />
            </LeftContainer>
          )}
          <Stack sx={(theme) => ({ marginTop: theme.spacing(3) })}>
            {enableListExport && (
              <Button
                variant="contained"
                disabled={currentlyDisplayedLists.length === 0}
                sx={{ width: '114px', height: '40px' }}
                onClick={() => setOpenExportModal(true)}
              >
                <Icons.Download
                  sx={(theme) => ({
                    height: '24px',
                    marginRight: theme.spacing(1.5),
                  })}
                />
                <Text sx={{ marginRight: 'auto' }} variant="body1">
                  Export
                </Text>
              </Button>
            )}
          </Stack>
        </Stack>
        <HorizontalLine />
        {isLoading || isBrandLoading ? (
          <>
            <LoadingReportCard />
            <LoadingReportCard />
          </>
        ) : (
          <>
            {accountListsLength > 0 ? (
              <>
                {currentlyDisplayedLists?.map((list: LizanoListInfo) => {
                  return (
                    <AccountListsList
                      list={list}
                      key={`list-${list?.id}`}
                      customHeaderActions={customHeaderActions}
                      triggerModal={triggerModal}
                      isInSingleReportVariation={isInSingleReportVariation}
                    />
                  );
                })}
              </>
            ) : (
              <EmptySectionCard
                name="NoListsCard"
                triggerModal={triggerModal}
                isInSingleReportVariation={isInSingleReportVariation}
              />
            )}
          </>
        )}
      </FlexContainer>
      <ExportModal
        openModal={openExportModal}
        setOpenModal={setOpenExportModal}
        lists={currentlyDisplayedLists}
      />
      {isInSingleReportVariation && (
        <PremiumDataModal
          isOpen={isModalOpen}
          onClose={closeModal}
          searchType={selectedOption}
          paymentShowModalNumber={paymentShowModalNumber}
          setPaymentShowModalNumber={setPaymentShowModalNumber}
          openSuccessModal={openSuccessModal}
        />
      )}
      <PremiumPaymentSuccessModal
        open={isSuccessModaVisible}
        onClose={closeModals}
        onAccept={onSuccessPaymentHandler}
      />
    </>
  );
}
